import React, { useMemo, useRef, useState } from 'react';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { SuperUserContext } from '../../../context/SuperUserContext';
import { UserContext } from '../../../context/UserContext';
import { SuperUserRole } from '../../../types/superUser';
import { TranslationKey } from '../../../types/translation/TranslationKey';
import { t } from '../../../types/translation/Translator';
import { MenuItemProps } from '../../../VentoryUI/components/common/Menu';
import { Routing } from '../../../util/routes.util';
import { ProfileAvatar } from '../Profile';
import { ClickAwayListener } from '@mui/material';
import Icon, { IconType } from '../../icons/Icon';
import { CompanyContext } from '../../../context/CompanyContext';
import { Company } from '../../../types/company';

interface HeaderUserMenuProps {}

export default function HeaderUserMenu({}: HeaderUserMenuProps) {
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);

  const { setCurrentCompany } = useContext(CompanyContext);
  const { currentUser, signOut } = useContext(UserContext);
  const { superUser } = useContext(SuperUserContext);

  const [open, setOpen] = useState(false);

  const menuItems: MenuItemProps[] = useMemo(() => {
    const shown: MenuItemProps[] = [];

    shown.push({
      text: t(TranslationKey.profile).singular.label,
      onClick: () => {
        setOpen(false);
        navigate(Routing.profile.personalInformation());
      },
      icon: <Icon type={IconType.settings1} size={'size-5'} />,
    });

    if (superUser) {
      shown.push({
        onClick: () => {
          setOpen(false);
          superUser?.role === SuperUserRole.translator ? navigate('/admin/translations') : navigate('/admin');
        },
        text: t(TranslationKey.administrator).singular.label,
        icon: <Icon type={IconType.key1} size={'size-5'} />,
      });
    }

    shown.push({
      onClick: () => {
        setOpen(false);
        setCurrentCompany(new Company({ companyId: '' }));
        navigate('/company');
      },
      text: t(TranslationKey.company).singular.label,
      icon: <Icon type={IconType.refreshCw5} size={'size-5'} />,
    });

    shown.push({
      onClick: () => {
        setOpen(false);
        signOut();
      },
      text: t(TranslationKey.signOut).singular.label,
      icon: <Icon type={IconType.logOut1} size={'size-5'} />,
    });

    return shown;
  }, []);

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div className='relative h-fit w-fit' style={{ lineHeight: 0 }} ref={ref}>
        <ProfileAvatar size={'sm'} onClick={() => setOpen(open => !open)} />

        {open && (
          <div className='absolute -bottom-1 right-0 z-40 translate-y-full leading-normal shadow-default'>
            <menu
              onMouseDown={event => event.preventDefault()}
              onMouseEnter={event => event.preventDefault()}
              className='rounded-2 border-gray-400 bg-white stroke-black p-2 text-black'
            >
              <div className='grid gap-2'>
                <button className='flex items-center gap-4 bg-gray-50 px-3 py-2 text-left'>
                  <ProfileAvatar size={'sm'} />

                  <span>
                    <p className='font-semibold text-black'>
                      {currentUser?.firstName} {currentUser?.lastName}
                    </p>
                    <p className='text-sm text-gray-600'>{currentUser?.email}</p>
                  </span>
                </button>

                <ul>
                  {menuItems.map(item => (
                    <li key={item.text}>
                      <button
                        onClick={() => item.onClick()}
                        className='w-full select-none whitespace-nowrap px-3 py-2 text-sm hover:bg-gray-100 active:bg-primary-50'
                      >
                        <span className='flex items-center gap-2'>
                          {item.icon}
                          <p>{item.text}</p>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </menu>
          </div>
        )}
      </div>
    </ClickAwayListener>
  );
}
